import { flatten, intersection, values } from "lodash"
import uid from "uid"

import { BooleanProductFilterModel } from "../../model/product-filter/boolean-product-filter-model"
import { DualNumberProductFilterModel } from "../../model/product-filter/dual-number-product-filter-model"
import { EffectProductFilterModel } from "../../model/product-filter/effect-product-filter-model"
import { EnumProductFilterModel } from "../../model/product-filter/enum-product-filter-model"
import { GroupedEnumProductFilterModel } from "../../model/product-filter/grouped-enum-product-filter-model"
import { NumberProductFilterModel } from "../../model/product-filter/number-product-filter-model"
import { RangeProductFilterModel } from "../../model/product-filter/range-product-filter-model"

const filterLifetime = 12 // hours

export class ProductsDatabase {
  _products
  _filters = {
    ENUM: [],
    EFFECT: [],
    NUMBER: [],
    DUAL_NUMBER: [],
    RANGE: [],
    BOOLEAN: [],
  }
  _updateFns = []
  _filteredProducts
  _preselected_markets

  constructor(products, productParameterGroups, preselectedMarkets) {
    this._products = products.sort((productA, productB) =>
      (productB.name || "").localeCompare(productA.name),
    )
    this._preselected_markets = preselectedMarkets

    this.initialStateRestored = false

    productParameterGroups.forEach((group) => {
      switch (group.type) {
        case "ENUM":
          this._filters.ENUM.push(new EnumProductFilterModel(group, products))
          break

        case "EFFECT":
          this._filters.EFFECT.push(
            new EffectProductFilterModel(group, products),
          )
          break

        case "NUMBER":
          this._filters.NUMBER.push(
            new NumberProductFilterModel(group, products),
          )
          break

        case "RANGE":
          this._filters.RANGE.push(new RangeProductFilterModel(group, products))
          break

        case "DUAL_NUMBER":
          this._filters.DUAL_NUMBER.push(
            new DualNumberProductFilterModel(group, products),
          )
          break

        case "BOOLEAN":
          this._filters.BOOLEAN.push(
            new BooleanProductFilterModel(group, products),
          )
          break
        default:
          break
      }
    })

    // group filters
    const groupedEnumFiltersByName = {}
    for (let group of this._filters.ENUM) {
      const name = group.name?.value_en || group.name.key
      if (name) {
        if (!groupedEnumFiltersByName[name]) {
          groupedEnumFiltersByName[name] = []
        }
        groupedEnumFiltersByName[name].push(group)
      }
    }

    const groupedEnumFilters = []
    for (let name in groupedEnumFiltersByName) {
      if (groupedEnumFiltersByName.hasOwnProperty(name)) {
        if (groupedEnumFiltersByName[name].length === 1) {
          groupedEnumFilters.push(groupedEnumFiltersByName[name][0])
        } else if (groupedEnumFiltersByName[name]) {
          groupedEnumFilters.push(
            new GroupedEnumProductFilterModel(
              groupedEnumFiltersByName[name],
              products,
            ),
          )
        }
      }
    }
    this._filters.ENUM = groupedEnumFilters

    for (let f in this._filters) {
      this._filters[f].forEach((group) => {
        group.onUpdate(() => this._updated())
      })
    }

    this._filteredProducts = this._filterProducts(this._products, this._filters)
  }

  _updated() {
    this._filteredProducts = this._filterProducts(this._products, this._filters)
    this._updateFns.forEach(([fn]) => fn())
  }

  _applyProductMatrix(matrix, allProducts, excludeFilterIndex = -1) {
    return allProducts.filter((product, i) => {
      for (let j = 0; j < matrix.length; j++) {
        if (j === excludeFilterIndex) {
          continue
        }

        if (!matrix[j][i]) {
          return false
        }
      }
      return true
    })
  }

  _filterProducts(allProducts, filters) {
    const allFilters = flatten(values(filters))

    const all = allProducts.map(() => true)

    const filterProductMatrix = allFilters.map((filter) => {
      if (!filter._isActive || filter.isHidden()) {
        return all
      }
      return allProducts.map((product) => filter.matches(product))
    })

    const filteredProducts = this._applyProductMatrix(
      filterProductMatrix,
      allProducts,
    )

    // update filters
    allFilters.forEach((filter, index) => {
      const filteredProductsWithoutCurrent = this._applyProductMatrix(
        filterProductMatrix,
        allProducts,
        index,
      )
      if (filter.updateValues) {
        filter.updateValues(
          filteredProducts,
          filteredProductsWithoutCurrent,
          this._filters.ENUM,
          this._preselected_markets,
        )
      }
    })

    return filteredProducts
  }

  getFilteredProducts() {
    return this._filteredProducts
  }

  getFilters() {
    return this._filters
  }

  isInitialStateRestored() {
    return this.initialStateRestored
  }

  getProductContext() {
    const res = {}

    if (this._preselected_markets && this._preselected_markets.length > 0) {
      res.market = { id: this._preselected_markets[0].id }
    } else {
      const marketFilter = flatten(values(this._filters)).find(
        (f) => f._id === -2,
      )
      if (marketFilter?.selected?.length === 1) {
        res.market = { id: marketFilter.selected[0].id }
      }
    }

    return res
  }

  resetFilters() {
    flatten(values(this._filters)).forEach((filter) => filter.reset())
    this._updated()
  }

  hasFilters() {
    for (const filter of flatten(values(this._filters))) {
      if (filter._isActive) {
        return true
      }
    }
    return false
  }

  getState() {
    const res = {}
    res.state = flatten(values(this._filters)).map((filter) =>
      filter.getState(),
    )
    res.time = new Date()
    return res
  }

  restoreState({ state, time }) {
    const now = new Date()

    if (
      state &&
      new Date(time) > now.setHours(now.getHours() - filterLifetime)
    ) {
      for (const filter of flatten(values(this._filters))) {
        const stateIndex = state.findIndex((s) => s._id === filter._id)
        if (stateIndex !== -1) {
          filter.restoreState(state[stateIndex])
        }
      }
      this._updated()

      if (this.hasFilters()) {
        this.initialStateRestored = true
      }
    }
  }

  loadSearchParams(searchParams, overwriteMarket = undefined) {
    if (!searchParams) {
      return
    }

    // load market
    if (searchParams.has("market")) {
      const filter = flatten(values(this._filters)).find((f) => f._id === -2)
      const value = filter?.values?.find(
        (v) => v.id === +searchParams.get("market"),
      )
      if (filter && value) {
        filter.externalReset()
        filter.select(value)
      }
    }
  }

  onUpdate(fn) {
    const fnId = uid()
    this._updateFns.push([fn, fnId])
    return {
      unsubscribe: () => this.removeOnUpdate(fnId),
    }
  }

  removeOnUpdate(id) {
    const index = this._updateFns.findIndex(([fn, fnId]) => fnId === id)
    if (index === -1) {
      console.error(
        "[products-database.js] The update function you tried to remove (" +
          id +
          ") was not found.",
        JSON.stringify(this._updateFns),
      )
    }
    this._updateFns.splice(index, 1)
  }

  destroy() {
    for (let f in this._filters) {
      this._filters[f].forEach((group) => {
        group.destroy()
      })
    }
  }
}
